import { useState, useRef, type KeyboardEvent } from 'react';
import styles from './InputBar.module.css';

interface InputBarProps {
  onSend: (message: string, files?: File[]) => void;
  teachMode: boolean;
  onToggleTeachMode: () => void;
  disabled?: boolean;
}

export function InputBar({ onSend, teachMode, onToggleTeachMode, disabled }: InputBarProps) {
  const [text, setText] = useState('');
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSend = () => {
    const hasText = text.trim().length > 0;
    const hasFiles = stagedFiles.length > 0;
    if ((!hasText && !hasFiles) || disabled) return;

    onSend(text || (hasFiles ? `Audit these ${stagedFiles.length} file(s)` : ''), hasFiles ? stagedFiles : undefined);
    setText('');
    setStagedFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setStagedFiles((prev) => [...prev, ...Array.from(files)]);
      e.target.value = '';
    }
  };

  const removeFile = (index: number) => {
    setStagedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const fileIcon = (name: string) => {
    const ext = name.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return '\u{1F4C4}';
    if (ext === 'xlsx' || ext === 'csv') return '\u{1F4CA}';
    if (ext === 'docx') return '\u{1F4DD}';
    if (['jpg', 'jpeg', 'png', 'heic'].includes(ext || '')) return '\u{1F5BC}';
    return '\u{1F4CE}';
  };

  return (
    <div className={styles.inputBar}>
      {teachMode && (
        <div className={styles.teachBanner}>
          Teach Mode: ON — Everything you say will be captured as KBW knowledge.
        </div>
      )}

      {stagedFiles.length > 0 && (
        <div className={styles.fileChips}>
          {stagedFiles.map((f, i) => (
            <div key={`${f.name}-${i}`} className={styles.fileChip}>
              <span className={styles.fileIcon}>{fileIcon(f.name)}</span>
              <span className={styles.fileName}>{f.name}</span>
              <span className={styles.fileSize}>
                {f.size < 1024 * 1024
                  ? `${Math.round(f.size / 1024)}KB`
                  : `${(f.size / (1024 * 1024)).toFixed(1)}MB`}
              </span>
              <button
                className={styles.fileRemove}
                onClick={() => removeFile(i)}
                title="Remove file"
              >
                &times;
              </button>
            </div>
          ))}
        </div>
      )}

      <div className={styles.inputRow}>
        <button
          className={styles.attachBtn}
          onClick={() => fileInputRef.current?.click()}
          title="Attach file"
        >
          &#x1F4CE;
        </button>
        <input
          ref={fileInputRef}
          type="file"
          className={styles.fileInput}
          onChange={handleFileChange}
          accept=".xml,.dxf,.jpg,.jpeg,.png,.heic,.csv,.xlsx,.pdf,.docx"
          multiple
        />
        <input
          className={styles.textInput}
          placeholder={stagedFiles.length > 0 ? `${stagedFiles.length} file(s) ready — type a message or hit Send` : 'Holler at Foreman...'}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
        />
        <button
          className={`${styles.teachBtn} ${teachMode ? styles.teachActive : ''}`}
          onClick={onToggleTeachMode}
          title="Toggle Teach Mode"
        >
          &#x1F393;
        </button>
        <button
          className={`${styles.sendBtn} ${(text.trim() || stagedFiles.length > 0) ? styles.sendActive : ''}`}
          onClick={handleSend}
          disabled={(!text.trim() && stagedFiles.length === 0) || disabled}
        >
          Send
        </button>
      </div>
    </div>
  );
}
